import { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { Search, X, Copy, Loader2, Users, Bot as BotIcon, Plus, Minus, Zap } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import MemberRolesTools from "@/pages/members/MemberRolesTools";

const TABS = [{ id: "all", label: "Tutti", icon: Users }, { id: "user", label: "Membri", icon: Users }, { id: "bot", label: "Bot", icon: BotIcon }];

export default function Members() {
  const [kind, setKind] = useState("all");
  const [query, setQuery] = useState("");
  const [roleId, setRoleId] = useState("");
  const [members, setMembers] = useState(null);
  const [total, setTotal] = useState(0);
  const [roles, setRoles] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [expanded, setExpanded] = useState(new Set());
  const [panel, setPanel] = useState(null);
  const [job, setJob] = useState(null);
  const [massOpen, setMassOpen] = useState(false);
  const [mgrBusy, setMgrBusy] = useState(null);
  const pollRef = useRef(null);

  const roleName = (id) => roles.find((r) => r.id === id)?.name || id;
  const roleColor = (id) => roles.find((r) => r.id === id)?.color || "#949BA4";

  const load = useCallback(async () => {
    setMembers(null);
    try {
      const res = await api.members({ kind: kind === "all" ? undefined : kind, query: query || undefined, roleId: roleId || undefined });
      setMembers(res.members); setTotal(res.total);
    } catch (e) { toast.error(apiError(e)); setMembers([]); }
  }, [kind, query, roleId]);

  useEffect(() => { api.roles().then(setRoles).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  // Membri entrati, usciti o con ruoli cambiati su Discord: la lista si aggiorna da sola.
  useEffect(() => {
    let t;
    const onChange = () => { clearTimeout(t); t = setTimeout(load, 800); };
    const onRoles = (e) => { if (/^(role\.|site\.refresh)/.test(e.detail?.event || "")) api.roles().then(setRoles).catch(() => {}); };
    window.addEventListener("kalyx:members", onChange);
    window.addEventListener("kalyx:live", onRoles);
    return () => { window.removeEventListener("kalyx:members", onChange); window.removeEventListener("kalyx:live", onRoles); clearTimeout(t); };
  }, [load]);
  useEffect(() => () => clearInterval(pollRef.current), []);

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const clearSel = () => setSelected(new Set());
  const toggleExpand = (id) => setExpanded((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allIds = (members || []).map((m) => m.id);
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id));
  const someSelected = allIds.some((id) => selected.has(id));
  const toggleAll = () => setSelected(() => allSelected ? new Set() : new Set(allIds));

  const pollJob = (id) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const j = await api.job(id); setJob(j);
        if (j.status === "done" || j.status === "error") {
          clearInterval(pollRef.current);
          toast.success(`Operazione completata: ${j.processed}/${j.total}${j.errors ? ` (${j.errors} errori)` : ""}`);
          await load(); clearSel();
          setTimeout(() => setJob(null), 2500);
        }
      } catch { clearInterval(pollRef.current); }
    }, 700);
  };

  const runBulk = async (action) => {
    if (selected.size === 0) return;
    const rid = document.getElementById("bulk-role")?.value;
    if (!rid) return toast.error("Scegli un ruolo");
    try {
      const fn = action === "add" ? api.applyRole : api.removeRole;
      const j = await fn(rid, "selected", Array.from(selected));
      setJob(j); pollJob(j.id);
    } catch (e) { toast.error(apiError(e)); }
  };

  const memberRoles = (m) => (m.roles || []).filter((rid) => !roles.find((r) => r.id === rid)?.isEveryone);

  const changeMemberRole = async (roleId, action) => {
    if (!panel) return;
    setMgrBusy(roleId);
    try {
      const fn = action === "add" ? api.memberAddRole : api.memberRemoveRole;
      const updated = await fn(panel.id, roleId);
      setPanel(updated);
      setMembers((ms) => (ms ? ms.map((m) => (m.id === updated.id ? updated : m)) : ms));
      toast.success(action === "add" ? "Ruolo aggiunto" : "Ruolo rimosso");
    } catch (e) { toast.error(apiError(e)); } finally { setMgrBusy(null); }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-white sm:text-3xl">Membri</h1>
          <p className="text-sm text-slate-400">{total} risultati · dati dalla cache del server</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <MemberRolesTools onDone={() => setTimeout(load, 2500)} />
          <Button onClick={() => setMassOpen(true)} data-testid="mass-op-button" className="rounded-xl bg-white/5 text-white hover:bg-white/10"><Zap className="mr-2 h-4 w-4 text-[#FEE75C]" />Operazioni massive</Button>
        </div>
      </div>

      {/* tabs + filtri */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex rounded-xl bg-[#12141D] p-1">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setKind(t.id)} data-testid={`member-tab-${t.id}`}
              className={`flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold transition-all ${kind === t.id ? "bg-[#5865F2] text-white" : "text-slate-400 hover:text-white"}`}>
              <t.icon className="h-4 w-4" />{t.label}
            </button>
          ))}
        </div>
        <div className="relative min-w-[200px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cerca nome, username o ID…" data-testid="member-search-input" className="rounded-xl border-white/10 bg-[#12141D] pl-9 text-white" />
        </div>
        <div className="w-52">
          <Select value={roleId || "all"} onValueChange={(v) => setRoleId(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="role-filter-select" className="rounded-xl border-white/10 bg-[#12141D] text-white"><SelectValue placeholder="Tutti i ruoli" /></SelectTrigger>
            <SelectContent className="border-white/10 bg-[#161923] text-slate-200">
              <SelectItem value="all" className="focus:bg-white/10">Tutti i ruoli</SelectItem>
              {roles.filter((r) => !r.isEveryone).map((r) => <SelectItem key={r.id} value={r.id} className="focus:bg-white/10">{r.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* job progress */}
      {job && (
        <div className="card-elevated p-4" data-testid="job-progress">
          <div className="mb-2 flex items-center justify-between text-sm"><span className="font-semibold text-white">{job.params?.action === "add" ? "Assegnazione" : "Rimozione"} ruolo «{job.params?.roleName}»</span><span className="font-mono text-slate-400">{job.processed}/{job.total}</span></div>
          <div className="h-2.5 w-full overflow-hidden rounded-full bg-[#0B0C10]"><div className="h-full rounded-full bg-gradient-to-r from-[#5865F2] to-[#00F0FF] transition-all" style={{ width: `${job.total ? Math.round((job.processed / job.total) * 100) : 0}%` }} /></div>
          <p className="mt-1 text-xs text-slate-500">Le operazioni rispettano i rate limit di Discord (queue lato backend).</p>
        </div>
      )}

      {/* bulk toolbar */}
      {selected.size > 0 && (
        <div className="card-elevated flex flex-wrap items-center gap-3 border-[#5865F2]/40 p-3" data-testid="bulk-toolbar">
          <span className="text-sm font-semibold text-white">{selected.size} selezionati</span>
          <select id="bulk-role" data-testid="bulk-role-select" className="rounded-lg border border-white/10 bg-[#0B0C10] px-3 py-1.5 text-sm text-white">
            <option value="">Scegli ruolo…</option>
            {roles.filter((r) => !r.isEveryone).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
          <Button size="sm" onClick={() => runBulk("add")} data-testid="bulk-assign" className="bg-[#248046] hover:bg-[#1a6334]"><Plus className="mr-1 h-4 w-4" />Assegna</Button>
          <Button size="sm" onClick={() => runBulk("remove")} data-testid="bulk-remove" className="bg-[#DA373C] hover:bg-[#a12828]"><Minus className="mr-1 h-4 w-4" />Rimuovi</Button>
          <Button size="sm" variant="ghost" onClick={clearSel} className="text-slate-400">Deseleziona</Button>
        </div>
      )}

      {/* lista */}
      <div className="card-elevated overflow-hidden" data-testid="members-list">
        {members === null && <div className="space-y-2 p-4">{[0,1,2,3,4].map(i => <Skeleton key={i} className="h-14 rounded-lg bg-white/5" />)}</div>}
        {members && members.length === 0 && <div className="p-10 text-center text-sm text-slate-500">Nessun membro trovato.</div>}
        {members && members.length > 0 && (
          <>
            <div className="flex items-center gap-3 border-b border-white/10 bg-[#0f111a] px-4 py-2.5" data-testid="members-list-header">
              <input type="checkbox" checked={allSelected} ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected; }}
                onChange={toggleAll} data-testid="select-all-checkbox" className="h-4 w-4 accent-[#5865F2]" />
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">{allSelected ? "Deseleziona tutti" : "Seleziona tutti"} · {members.length} in elenco</span>
            </div>
            <div className="divide-y divide-white/5">
            {members.map((m) => (
              <div key={m.id} className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-white/5" data-testid={`member-row-${m.id}`}>
                <input type="checkbox" checked={selected.has(m.id)} onChange={() => toggle(m.id)} data-testid={`member-check-${m.id}`} className="h-4 w-4 accent-[#5865F2]" />
                <img src={m.avatar} alt="" className="h-9 w-9 rounded-full object-cover" />
                <button onClick={() => setPanel(m)} className="min-w-0 flex-1 text-left">
                  <p className="flex items-center gap-2 truncate font-semibold text-white">{m.nick || m.displayName}
                    {m.bot && <span className="rounded bg-[#5865F2] px-1 text-[10px] font-bold">BOT</span>}
                    {m.presence && !m.bot && <span className="h-2 w-2 rounded-full" style={{ background: { online: "#23A55A", idle: "#F0B232", dnd: "#F23F43", offline: "#80848E" }[m.presence] }} />}
                  </p>
                  <p className="truncate text-xs text-slate-500">@{m.username}</p>
                </button>
                <div className="hidden flex-wrap justify-end gap-1 sm:flex" data-testid={`member-roles-${m.id}`}>
                  {(expanded.has(m.id) ? memberRoles(m) : memberRoles(m).slice(0, 3)).map((rid) => (
                    <Badge key={rid} variant="outline" className="text-[10px]" style={{ borderColor: roleColor(rid) + "66", color: roleColor(rid) }}>{roleName(rid)}</Badge>
                  ))}
                  {memberRoles(m).length > 3 && !expanded.has(m.id) && (
                    <button onClick={() => toggleExpand(m.id)} data-testid={`member-roles-expand-${m.id}`}
                      className="rounded border border-white/10 px-1.5 py-0.5 text-[10px] font-semibold text-slate-400 hover:border-[#5865F2] hover:text-white">
                      +{memberRoles(m).length - 3}
                    </button>
                  )}
                  {memberRoles(m).length > 3 && expanded.has(m.id) && (
                    <button onClick={() => toggleExpand(m.id)} data-testid={`member-roles-collapse-${m.id}`}
                      className="rounded border border-white/10 px-1.5 py-0.5 text-[10px] font-semibold text-slate-400 hover:border-[#5865F2] hover:text-white">
                      − Comprimi
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          </>
        )}
      </div>

      {/* side panel */}
      {panel && createPortal((
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setPanel(null)} />
          <div className="relative h-full w-full max-w-md animate-fade-up overflow-y-auto border-l border-white/10 bg-[#12141D] p-6" data-testid="member-panel">
            <button onClick={() => setPanel(null)} className="absolute right-4 top-4 text-slate-400 hover:text-white"><X className="h-5 w-5" /></button>
            <div className="flex flex-col items-center text-center">
              <img src={panel.avatar} alt="" className="h-20 w-20 rounded-full object-cover ring-2 ring-white/10" />
              <h2 className="mt-3 text-xl font-bold text-white">{panel.nick || panel.displayName}</h2>
              <p className="text-sm text-slate-400">@{panel.username}</p>
              <button onClick={() => { navigator.clipboard?.writeText(panel.id); toast.success("ID copiato"); }} className="mt-1 flex items-center gap-1 font-mono text-xs text-slate-500 hover:text-white"><Copy className="h-3 w-3" />{panel.id}</button>
            </div>
            <div className="mt-6 space-y-2 rounded-xl border border-white/10 bg-[#0B0C10] p-4 text-sm">
              <div className="flex justify-between"><span className="text-slate-400">Entrato</span><span className="text-white">{panel.joinedAt ? new Date(panel.joinedAt).toLocaleDateString("it-IT") : "—"}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Account creato</span><span className="text-white">{panel.createdAt ? new Date(panel.createdAt).toLocaleDateString("it-IT") : "—"}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Tipo</span><span className="text-white">{panel.bot ? "Bot" : "Utente"}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Nº ruoli</span><span className="font-mono text-white">{memberRoles(panel).length}</span></div>
            </div>
            <div className="mt-4">
              <p className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-500">Ruoli assegnati</p>
              <div className="flex flex-wrap gap-1.5" data-testid="panel-member-roles">
                {memberRoles(panel).length === 0 && <span className="text-sm text-slate-500">Nessun ruolo</span>}
                {memberRoles(panel).map((rid) => (
                  <span key={rid} className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs" style={{ borderColor: roleColor(rid) + "66", color: roleColor(rid) }}>
                    {roleName(rid)}
                    <button onClick={() => changeMemberRole(rid, "remove")} disabled={mgrBusy === rid} data-testid={`panel-remove-role-${rid}`} className="ml-0.5 rounded-full hover:text-white disabled:opacity-40">
                      {mgrBusy === rid ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
                    </button>
                  </span>
                ))}
              </div>
            </div>
            <div className="mt-4">
              <p className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-500">Aggiungi un ruolo</p>
              <Select value="" onValueChange={(rid) => changeMemberRole(rid, "add")} disabled={!!mgrBusy}>
                <SelectTrigger data-testid="panel-add-role-select" className="rounded-xl border-white/10 bg-[#0B0C10] text-white"><SelectValue placeholder="Scegli un ruolo da assegnare…" /></SelectTrigger>
                <SelectContent className="max-h-60 border-white/10 bg-[#161923] text-slate-200">
                  {roles.filter((r) => !r.isEveryone && !(panel.roles || []).includes(r.id)).map((r) => (
                    <SelectItem key={r.id} value={r.id} className="focus:bg-white/10"><span className="mr-2 inline-block h-2 w-2 rounded-full align-middle" style={{ background: r.color }} />{r.name}</SelectItem>
                  ))}
                  {roles.filter((r) => !r.isEveryone && !(panel.roles || []).includes(r.id)).length === 0 && <div className="px-2 py-1.5 text-xs text-slate-500">Tutti i ruoli sono già assegnati</div>}
                </SelectContent>
              </Select>
              <p className="mt-2 text-xs text-slate-500">Le modifiche vengono applicate subito sul server tramite il bot (rispettando la gerarchia di Discord).</p>
            </div>
          </div>
        </div>
      ), document.body)}

      {/* mass op dialog */}
      <MassOpDialog open={massOpen} onClose={() => setMassOpen(false)} roles={roles} onStart={(j) => { setJob(j); pollJob(j.id); setMassOpen(false); }} />
    </div>
  );
}

function MassOpDialog({ open, onClose, roles, onStart }) {
  const [roleId, setRoleId] = useState("");
  const [target, setTarget] = useState("all");
  const [action, setAction] = useState("add");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const start = async () => {
    if (!roleId) return toast.error("Scegli un ruolo");
    setBusy(true);
    try {
      const fn = action === "add" ? api.applyRole : api.removeRole;
      const j = await fn(roleId, target);
      toast.success(`Operazione avviata su ${j.total} membri`);
      onStart(j); setConfirm(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const roleLabel = roles.find((r) => r.id === roleId)?.name;
  const sc = "rounded-lg border-white/10 bg-[#0B0C10] text-white";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-white/10 bg-[#12141D] text-slate-200">
        <DialogHeader><DialogTitle className="text-white">Operazioni massive sui ruoli</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-400">Applica o rimuovi un ruolo a un insieme di membri. Prima dell'operazione viene creato automaticamente un backup pre-operazione.</p>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="mb-1 block text-xs font-bold uppercase text-slate-500">Azione</label>
            <Select value={action} onValueChange={setAction}><SelectTrigger className={sc}><SelectValue /></SelectTrigger>
              <SelectContent className="border-white/10 bg-[#161923] text-slate-200"><SelectItem value="add" className="focus:bg-white/10">Applica ruolo</SelectItem><SelectItem value="remove" className="focus:bg-white/10">Rimuovi ruolo</SelectItem></SelectContent></Select>
          </div>
          <div><label className="mb-1 block text-xs font-bold uppercase text-slate-500">Ruolo</label>
            <Select value={roleId} onValueChange={setRoleId}><SelectTrigger data-testid="massop-role" className={sc}><SelectValue placeholder="Scegli…" /></SelectTrigger>
              <SelectContent className="border-white/10 bg-[#161923] text-slate-200 max-h-60">{roles.filter((r) => !r.isEveryone).map((r) => <SelectItem key={r.id} value={r.id} className="focus:bg-white/10">{r.name}</SelectItem>)}</SelectContent></Select>
          </div>
        </div>
        <div><label className="mb-1 block text-xs font-bold uppercase text-slate-500">Applica a</label>
          <Select value={target} onValueChange={setTarget}><SelectTrigger data-testid="massop-target" className={sc}><SelectValue /></SelectTrigger>
            <SelectContent className="border-white/10 bg-[#161923] text-slate-200">
              <SelectItem value="all" className="focus:bg-white/10">Tutti i membri</SelectItem>
              <SelectItem value="users" className="focus:bg-white/10">Solo utenti</SelectItem>
              <SelectItem value="bots" className="focus:bg-white/10">Solo bot</SelectItem>
            </SelectContent></Select>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-slate-400">Annulla</Button>
          <Button onClick={() => setConfirm(true)} disabled={!roleId} data-testid="massop-start" className="bg-[#5865F2] hover:bg-[#4752C4]">Continua</Button>
        </DialogFooter>

        <Dialog open={confirm} onOpenChange={setConfirm}>
          <DialogContent className="border-white/10 bg-[#12141D] text-slate-200">
            <DialogHeader><DialogTitle className="text-white">Confermi l'operazione?</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-300">Vuoi <b>{action === "add" ? "applicare" : "rimuovere"}</b> il ruolo <b style={{ color: roles.find(r=>r.id===roleId)?.color }}>{roleLabel}</b> a <b>{{ all: "tutti i membri", users: "tutti gli utenti", bots: "tutti i bot" }[target]}</b>?</p>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setConfirm(false)} className="text-slate-400">No</Button>
              <Button onClick={start} disabled={busy} data-testid="massop-confirm" className="bg-[#5865F2] hover:bg-[#4752C4]">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Conferma</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
